import { PropType, computed, defineComponent, onUnmounted, ref, watch } from 'vue'
import { Store } from 'pinia'
import { gcj02ToBd09 } from '@/utils/coordinateTransform'
import { distanceFormat } from '@/utils/distance'
import { getPowerResourceType } from '@/pages/chargermap/utils/resource'
import { useRoute, useRouter } from 'vue-router'
import { MapViewMode, Marker, MarkerType, Point, ZoomMode } from '../../contract/map'
import { ChargeMapContext } from '../chargerMap/index.model'
import { ComponentActions, ComponentState } from '../../nsh5/localStores/component'
import { EnergyPlanNewDataModel, EnergyResourceModel } from '../../models/plan.model'
import {
  ResourceIconSizeMap,
  ResourceIndexMap,
  ResourceRoutePlanIconMap,
  ResourceRoutePlanIconPointMap,
  ResourceStandbyIndexMap,
} from '../chargerMap/mapConfig'
import { adjustSizeWithZoom, calcSize, changeMarker, fitBound, flyTo } from '../chargerMap/mapUtils'

/**
 * 路径规划渲染功能模块
 */
export type EnergyPlanProps = {
  onClickedMarker: (marker: Marker) => void
}
export default defineComponent({
  name: 'energy-plans-view',
  props: {
    mapContextRef: {
      type: Object as PropType<ChargeMapContext>,
    },
    mapInstance: {
      type: Object,
    },
    componentStore: {
      // 用于同一项目功能模块间的通信
      type: Object as PropType<Store<string, ComponentState, {}, ComponentActions>>,
      default: null,
    },
  },
  setup: (props, { expose }) => {
    const mapContextRef = computed(() => props.mapContextRef)
    const mapInstance = computed(() => props.mapInstance)
    const zoomLevel = ref<number>(0)
    const router = useRouter()
    const route = useRoute()
    const clickedMarker = ref<Marker | null>() // 选中的点

    let energyPlansCache: EnergyPlanNewDataModel
    let energyPlanStandby = [] as string[]
    let energyPlanResourceStandby: EnergyResourceModel[] = [] // 路径规划备选点资源
    let cacheClickedId = '' // 记录已经点击变大/已点击 的点

    const drawEnergyPlan = (
      context: ChargeMapContext,
      energyPlan: EnergyPlanNewDataModel,
      index: number,
      strokeWeight = 2
    ) => {
      let fitBoundPoints: Point[] = [] // 用于调整视野
      let lastDrawPolyline: Point[] = []
      let resourceStandbys = [] as EnergyResourceModel[] // 普通资源点（备选点）
      // 清除普通资源点（备选点）
      energyPlanResourceStandby = []
      energyPlan.plans?.forEach(
        (plan: { steps: any; resources: EnergyResourceModel[] }, i: number) => {
          const origItems = plan.resources.filter((item) => item.stationType == 'orig')
          const destItems = plan.resources.filter((item) => item.stationType == 'dest')
          // ----画线----
          let recordPoints: Point[] = []
          const origPosition = origItems[0]?.location.split(',')
          const orig = gcj02ToBd09({
            lng: Number(origPosition[0]),
            lat: Number(origPosition[1]),
          })
          const destPosition = destItems[0]?.location.split(',')
          const dest = gcj02ToBd09({
            lng: Number(destPosition[0]),
            lat: Number(destPosition[1]),
          })

          const steps = plan.steps
          if (steps && steps.length) {
            steps.forEach((item: { polyline: string }) => {
              const polylineList = item.polyline.split(';').map((line: string) => {
                const position = line.split(',')
                return gcj02ToBd09({
                  lng: Number(position[0]),
                  lat: Number(position[1]),
                })
              })
              recordPoints = recordPoints.concat(polylineList)
            })
            if (index == i) {
              lastDrawPolyline = recordPoints
            } else {
              context.addPolyline([
                {
                  points: recordPoints,
                  options: {
                    strokeWeight,
                    strokeColor: (process.config as any).style.route_line.energy_plan_standby,
                  },
                },
              ])
            }
            fitBoundPoints = fitBoundPoints.concat(recordPoints)
          }
          // ----------
          if (index == i) {
            let distanceEndToLastResource = ''
            // ----画推荐资源点----
            const markers = [] as Marker[]
            plan.resources.forEach((item: EnergyResourceModel) => {
              if (item.isCandidate) {
                const label = distanceFormat(item.prevCandidateDistance)
                distanceEndToLastResource = distanceFormat(item.nextCandidateDistance)
                const resourceObj = item.resource
                const type = getPowerResourceType(resourceObj)
                const position = resourceObj.location.split(',')
                const point = gcj02ToBd09({
                  lng: Number(position[0]),
                  lat: Number(position[1]),
                })
                markers.push({
                  id: resourceObj.id,
                  type: MarkerType.Icon,
                  loc: point,
                  label: label,
                  clickable: true,
                  zoomable: false,
                  size: ResourceIconSizeMap[ZoomMode.Route],
                  icon: ResourceRoutePlanIconMap[type],
                  zIndex: ResourceIndexMap[type],
                  data: resourceObj,
                })
                // 排除清除点
                energyPlanStandby = energyPlanStandby.filter((id) => {
                  return id === resourceObj.id
                })
              }
            })
            if (markers && markers.length > 0) {
              markers[0].label = '距起点' + markers[0].label
            }
            // 备用资源点
            resourceStandbys = plan.resources.filter((item) => item.stationType == 'resource') // 缓存普通资源点（备选点）
            energyPlanResourceStandby = resourceStandbys
            context.addMarkers(markers)
            // -----------
            // 画起点和终点
            const origMarker = {
              id: 'orig',
              type: MarkerType.Icon,
              loc: orig,
              size: { width: 30, height: 30 },
              icon: 'https://cdn-app.nio.com/pe/ns/h5/route/pin/start-pin.png',
            }
            const destMarker = {
              id: 'dest',
              type: MarkerType.Icon,
              loc: dest,
              label: distanceEndToLastResource,
              size: { width: 30, height: 30 },
              icon: 'https://cdn-app.nio.com/pe/ns/h5/route/pin/end-pin.png',
            }
            context.addMarkers([origMarker, destMarker])
          }
        }
      )

      // 最后画所选路线，防止被覆盖
      context.addPolyline([
        {
          points: lastDrawPolyline,
          options: {
            strokeWeight,
            strokeColor: (process.config as any).style.route_line.energy_plan,
          },
        },
      ])

      const pointList =
        (fitBoundPoints.length > 0 &&
          fitBoundPoints.map((item) => {
            return {
              lng: item.lng,
              lat: item.lat,
            }
          })) ||
        ([] as Point[])
      pointList.length > 0 && context.adjustCenterAndZoom(fitBound(pointList))
      drawEnergyPlanResourceStandby(context, resourceStandbys, zoomLevel.value)
    }

    // 路径规划画普通资源点（备选点）
    const drawEnergyPlanResourceStandby = (
      context: ChargeMapContext,
      resources: EnergyResourceModel[],
      zoomLevel?: number
    ) => {
      // 清理资源点重绘
      energyPlanStandby.length > 0 && context.clearMarkers(energyPlanStandby)
      energyPlanStandby = []
      const markers = [] as Marker[]

      resources.forEach((item: EnergyResourceModel) => {
        const resourceObj = item.resource
        const type = getPowerResourceType(resourceObj)
        const position = resourceObj.location.split(',')
        const point = gcj02ToBd09({
          lng: Number(position[0]),
          lat: Number(position[1]),
        })

        let varSize = ResourceIconSizeMap[ZoomMode.Route]
        let varIcon = ResourceRoutePlanIconMap[type]
        if (zoomLevel && zoomLevel < 9) {
          const w = calcSize(zoomLevel).w
          varSize = {
            width: w,
            height: w,
          }
          varIcon = ResourceRoutePlanIconPointMap[type]
        }
        energyPlanStandby.push(item.id) // 记录marker点，以后可以通过id修改marker
        markers.push({
          id: item.id,
          type: MarkerType.Icon,
          loc: point,
          clickable: (zoomLevel && zoomLevel >= 9) as boolean | undefined,
          zoomable: false,
          size: varSize,
          icon: varIcon,
          zIndex: ResourceStandbyIndexMap[type],
          data: resourceObj,
        })
      })
      context.addMarkers(markers)
    }

    props.componentStore.bus.on('onEnergyPlan', async (multiPlan: any) => {
      onEnergyPlan(multiPlan)
    })

    const onEnergyPlan = async (multiPlan: any) => {
      if (route.name === 'plan') {
        mapInstance.value?.clearOverlays()
        const index = multiPlan.index
        const energyPlans = multiPlan?.energyPlans
        if (energyPlans) {
          energyPlansCache = energyPlans
          drawEnergyPlan(mapContextRef.value!, energyPlans, index)
        } else {
          drawEnergyPlan(mapContextRef.value!, energyPlansCache, index)
        }
      }
    }

    props.componentStore.bus.on('onCloseEnergyPlan', () => {
      onCloseEnergyPlan()
    })
    const onCloseEnergyPlan = () => {
      mapInstance.value?.clearOverlays()
      if (props.componentStore.backPath) {
        const temp = props.componentStore.backPath
        window.history.replaceState(null, '', window.location.href + '?' + temp.slice(1))
        router.replace(props.componentStore.backPath)
      }
      props.componentStore.unsetDestLocationByPin() // 清除气泡
    }

    // ------------暴露出去的方法--------------
    /**
     * 通过id获取marker
     */
    props.componentStore.bus.on('onClickedMarkerByIdEnergyPlan', (id: string) => {
      const marker = mapContextRef.value?.getMarkerById(id)
      if (marker) {
        onClickedMarker(mapContextRef.value?.getMarkerById(id).customData.marker)
      }
    })
    /**
     * 点击地图上的点
     * @param customMarkerData
     * @returns
     */
    const onClickedMarker = (customMarkerData: Marker) => {
      if (cacheClickedId == customMarkerData.id) {
        // 防止对同一个点多次点击
        return
      }
      cacheClickedId = customMarkerData.id
      if (props.componentStore.viewMode === MapViewMode.EnergyPlan) {
        // 其他地图模式(路径规划) 只变大出现气泡
        const resource: any = customMarkerData?.data
        changeMarker(mapContextRef.value!, customMarkerData.id, clickedMarker.value?.id || '')
        clickedMarker.value = customMarkerData
        // 设置目的地，显示小气泡
        props.componentStore.setDestLocationByPin({
          name: resource.name,
          lng: customMarkerData.loc.lng,
          lat: customMarkerData.loc.lat,
          address: resource.address,
        })
      }
    }
    props.componentStore.bus.on('energyPlansClickedMarker', onClickedMarker)

    const zoomEndPlan = async () => {
      zoomLevel.value = mapInstance.value?.getZoom()
      if (props.componentStore.viewMode === MapViewMode.EnergyPlan) {
        props.componentStore.bus.emit('resetClickedMarker') // 缩放还原点击的marker
        adjustSizeWithZoom(mapContextRef.value!, zoomLevel.value)
        energyPlanResourceStandby &&
          energyPlanResourceStandby.length > 0 &&
          drawEnergyPlanResourceStandby(
            mapContextRef.value!,
            energyPlanResourceStandby,
            zoomLevel.value
          )
      }
    }

    // 启动相关事件监听
    const initEventListener = () => {
      mapInstance.value?.addEventListener('zoomend', zoomEndPlan, false)
    }

    expose({
      onClickedMarker,
    })

    // unmounted时摧毁相关事件监听
    const destroyEventListener = () => {
      mapInstance.value?.removeEventListener('zoomend', zoomEndPlan, false)
    }

    onUnmounted(() => {
      destroyEventListener()
      props.componentStore.bus.off('onEnergyPlan')
      props.componentStore.bus.off('onCloseEnergyPlan')
      props.componentStore.bus.off('onClickedMarkerByIdEnergyPlan')
      props.componentStore.bus.off('energyPlansClickedMarker')
    })

    /**
     * 组件获取地图实例后的回调
     */
    const onReady = () => {
      // mapInstance.value = mapContextRef.value?.getMap()
      initEventListener()
    }
    watch(
      () => props.mapContextRef,
      () => {
        if (props.mapContextRef) {
          // mapContextRef.value = props.mapContextRef
          onReady()
        }
      },
      {
        immediate: true,
      }
    )

    // --------------------------
    watch(
      () => props.componentStore.viewMode,
      () => {
        if (props.componentStore.viewMode !== MapViewMode.EnergyPlan) {
          onCloseEnergyPlan()
          props.componentStore.bus.emit('showIconFromRecommend')
          if (route.name == 'pin' || route.name == 'resource') {
            props.componentStore.removeCachePosition().then((cache: any) => {
              flyTo(mapContextRef.value!, cache.centerCache, cache.zoomLevelCache)
            })
          }
          clickedMarker.value = null
          cacheClickedId = ''
        }
      }
    )
    return () => {}
  },
})
